import { api } from "./client";

const base = (slug: string) => `/v1/c/${slug}`;

export type SuggestionKind =
  "playbook_reply" | "prompt_change" | "flow_question" | "knowledge_doc";
export interface LearningReportRow {
  id: string;
  periodFrom: string;
  periodTo: string;
  status: "generating" | "ready" | "failed" | "archived";
  trigger: "manual" | "weekly";
  stats: {
    conversationsAnalyzed?: number;
    successful?: number;
    unsuccessful?: number;
    byOutcome?: Record<string, number>;
    sampledMessages?: number;
  } | null;
  summary: string | null;
  aiCostMicros: number;
  createdAt: string;
  completedAt: string | null;
  suggestions: {
    proposed: number;
    approved: number;
    rejected: number;
    applied: number;
  };
}
export interface SuggestionRow {
  id: string;
  kind: SuggestionKind;
  title: string;
  body: string;
  rationale: string | null;
  evidence: Array<{ conversationId: string; excerpt?: string }>;
  successRate: number | null;
  sampleSize: number;
  target: {
    assistantId?: string;
    collectionId?: string;
    teamId?: string;
    flowId?: string;
  };
  status: "proposed" | "approved" | "rejected" | "applied";
  decidedBy: string | null;
  decidedAt: string | null;
  appliedRef: Record<string, string> | null;
  createdAt: string;
}
export interface OutcomeSuggestion {
  code: string;
  confidence: number;
  reason: string;
}

export const learningApi = {
  reports: (slug: string) =>
    api<{ reports: LearningReportRow[] }>(`${base(slug)}/learning/reports`),
  generate: (slug: string, body: { from?: string; to?: string } = {}) =>
    api<{ report: { id: string; status: string } }>(
      `${base(slug)}/learning/reports`,
      { method: "POST", body },
    ),
  report: (slug: string, id: string) =>
    api<{ report: LearningReportRow; suggestions: SuggestionRow[] }>(
      `${base(slug)}/learning/reports/${id}`,
    ),
  archive: (slug: string, id: string) =>
    api(`${base(slug)}/learning/reports/${id}`, {
      method: "PATCH",
      body: { status: "archived" },
    }),
  approve: (
    slug: string,
    id: string,
    body: {
      target?: { collectionId?: string; assistantId?: string; teamId?: string };
      body?: string;
    },
  ) =>
    api<{
      ok: true;
      status: string;
      appliedRef: Record<string, string> | null;
    }>(`${base(slug)}/learning/suggestions/${id}/approve`, {
      method: "POST",
      body,
    }),
  reject: (slug: string, id: string, reason?: string) =>
    api(`${base(slug)}/learning/suggestions/${id}/reject`, {
      method: "POST",
      body: reason ? { reason } : {},
    }),
  brief: (slug: string, conversationId: string) =>
    api<{ brief: string; updatedAt: string }>(
      `${base(slug)}/conversations/${conversationId}/brief`,
      { method: "POST", body: {} },
    ),
  outcomeSuggestion: (slug: string, conversationId: string) =>
    api<{ suggestion: OutcomeSuggestion | null }>(
      `${base(slug)}/conversations/${conversationId}/outcome-suggestion`,
    ),
};
